import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { PH_MASK_CLASS } from "../../features/analytics/sessionRecordingMasking.js";
import { FeedbackCell } from "../recordingAssessmentEditor/graderFeedback/feedbackCell.js";
import {
  getFeedbackAttributionText,
  getFeedbackCallDisplayLines,
  getFeedbackCurrentStateReason,
  getFeedbackExpectedGradeDisplayValue,
  getFeedbackGradeCorrectness,
  getFeedbackGradeDisplayValue,
} from "./feedbackReviewDisplayHelpers.js";

/**
 * @file Cells for the feedback review table. Contact names, commentary and
 * explanations are customer content, so every line carrying them is masked out
 * of session recordings.
 */

export interface FeedbackReviewCallCellProps {
  feedback: FeedbackWithCurrentGrade;
}

/** Which call the feedback came from: contact, secondary contact, call date. */
export const FeedbackReviewCallCell: FC<FeedbackReviewCallCellProps> = (
  props,
) => {
  const { feedback } = props;
  const { primaryName, secondaryName, recordedAtLabel } =
    getFeedbackCallDisplayLines(feedback);

  return (
    // space-y-1 in every cell of the row, so the stacked lines share the same
    // horizontal rhythm across columns.
    <div className="space-y-1">
      <div
        className={twMerge(
          "truncate font-medium text-foreground",
          PH_MASK_CLASS,
        )}
      >
        {primaryName}
      </div>
      {secondaryName ? (
        <div
          className={twMerge("truncate text-xs text-gray-600", PH_MASK_CLASS)}
        >
          {secondaryName}
        </div>
      ) : null}
      <div className="text-xs text-gray-600">{recordedAtLabel}</div>
    </div>
  );
};

export interface FeedbackReviewGradeCellProps {
  feedback: FeedbackWithCurrentGrade;
}

/** The grade the reviewer disagreed with, as it stood when they submitted. */
export const FeedbackReviewGradeCell: FC<FeedbackReviewGradeCellProps> = (
  props,
) => {
  const { feedback } = props;
  const commentary = feedback.data.grading.commentary;

  return (
    <div className="space-y-1">
      <div className="font-medium text-foreground">
        {getFeedbackGradeDisplayValue(feedback, feedback.data.grading.value)}
      </div>
      {commentary ? (
        <div className={twMerge("text-xs text-gray-600", PH_MASK_CLASS)}>
          {commentary}
        </div>
      ) : null}
    </div>
  );
};

export interface FeedbackReviewExpectedGradeCellProps {
  feedback: FeedbackWithCurrentGrade;
}

/**
 * What the reviewer said the grade should be. Submissions without an expected
 * value are explanation-only, so the value line (and its correctness icon)
 * drops out and the explanation leads.
 */
export const FeedbackReviewExpectedGradeCell: FC<
  FeedbackReviewExpectedGradeCellProps
> = (props) => {
  const { feedback } = props;
  const expectedGradeDisplayValue =
    getFeedbackExpectedGradeDisplayValue(feedback);
  const explanation = feedback.data.explanation;
  const currentStateReason = getFeedbackCurrentStateReason(feedback);
  const statusLine = currentStateReason ?? getFeedbackAttributionText(feedback);

  return (
    <div className="space-y-1">
      {expectedGradeDisplayValue !== undefined ? (
        <FeedbackCell
          value={expectedGradeDisplayValue}
          correctness={getFeedbackGradeCorrectness(feedback)}
        />
      ) : null}
      {explanation ? (
        <div className={twMerge("text-xs text-foreground", PH_MASK_CLASS)}>
          {explanation}
        </div>
      ) : null}
      <div className="text-xs text-gray-500">{statusLine}</div>
    </div>
  );
};
